import React, { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import Button from '../../../components/Button/Button'
import Card from '../../../components/Card/Card'
import Notification from '../../../components/Notification/Notification'
import { WORLD_CUP_STAGES } from '../../../graphql/queries'
import { UPDATE_WORLD_CUP_STAGES } from '../../../graphql/mutations'
import { DEFAULT_STAGES } from './worldCupAdmin.constants'
import useNotification from '../../../hooks/useNotification'

export default function WorldCupStagesAdmin() {
  const { showNotification, notificationProps } = useNotification()
  const [stageInputs, setStageInputs] = useState(DEFAULT_STAGES)

  const { data: stagesData, refetch: refetchStages } = useQuery(WORLD_CUP_STAGES, {
    fetchPolicy: 'cache-and-network',
  })

  useEffect(() => {
    if (Array.isArray(stagesData?.worldCupStages) && stagesData.worldCupStages.length > 0) {
      setStageInputs([...stagesData.worldCupStages].sort((a, b) => a.sortOrder - b.sortOrder))
    }
  }, [stagesData])

  const [saveStages, { loading: savingStages }] = useMutation(UPDATE_WORLD_CUP_STAGES, {
    onCompleted: async () => {
      showNotification('VM-faser gemt', 'success')
      await refetchStages()
    },
    onError: (e) => showNotification(e.message || 'Kunne ikke gemme faser', 'error'),
  })

  const handleStageChange = (index, field, value) => {
    setStageInputs((prev) => {
      const next = [...prev]
      next[index] = {
        ...next[index],
        [field]: field === 'sortOrder' ? Number(value) : value,
      }
      return next
    })
  }

  const handleAddStage = () => {
    const nextOrder = Math.max(0, ...stageInputs.map((s) => Number(s.sortOrder) || 0)) + 1
    setStageInputs((prev) => [...prev, { name: '', sortOrder: nextOrder }])
  }

  const handleRemoveStage = (index) => {
    setStageInputs((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)))
  }

  const handleSaveStages = async () => {
    const validStages = stageInputs
      .map((s) => ({ name: s.name.trim(), sortOrder: Number(s.sortOrder) }))
      .filter((s) => s.name && Number.isInteger(s.sortOrder) && s.sortOrder >= 1)

    if (validStages.length === 0) {
      showNotification('Tilføj mindst én fase', 'error')
      return
    }

    const names = new Set(validStages.map((s) => s.name.toLowerCase()))
    const orders = new Set(validStages.map((s) => s.sortOrder))
    if (names.size !== validStages.length) {
      showNotification('Fasenavne skal være unikke', 'error')
      return
    }
    if (orders.size !== validStages.length) {
      showNotification('SortOrder skal være unikke', 'error')
      return
    }

    await saveStages({ variables: { stages: validStages } })
  }

  return (
    <>
      <Notification {...notificationProps} />
      <Card className="worldcup-admin-card" hover={false}>
        <h3>VM-faser og sortOrder</h3>
        <p className="worldcup-admin-help">
          Definer turneringens faser og deres rækkefølge. Når du vælger en fase ved oprettelse af kampe, sættes sortOrder automatisk.
        </p>
        <div className="worldcup-admin-teams-header">
          <span className="worldcup-admin-teams-count">{stageInputs.length} faser</span>
          <Button title="+ Tilføj fase" onClick={handleAddStage} variant="outlined" />
        </div>
        <div className="worldcup-admin-stages-list">
          {stageInputs.map((stage, index) => (
            <div key={index} className="worldcup-admin-stage-row">
              <input
                className="worldcup-admin-input worldcup-admin-input--sort"
                type="number"
                min={1}
                value={stage.sortOrder}
                onChange={(e) => handleStageChange(index, 'sortOrder', e.target.value)}
                aria-label={`SortOrder for fase ${index + 1}`}
              />
              <input
                className="worldcup-admin-input"
                type="text"
                value={stage.name}
                placeholder={`Fase ${index + 1}`}
                onChange={(e) => handleStageChange(index, 'name', e.target.value)}
              />
              <button
                type="button"
                className="worldcup-admin-remove-team"
                onClick={() => handleRemoveStage(index)}
                disabled={stageInputs.length <= 1}
                aria-label="Fjern fase"
              >
                ×
              </button>
            </div>
          ))}
        </div>
        <div className="worldcup-admin-form-actions">
          <Button title="Gem VM-faser" onClick={handleSaveStages} color="confirmation" loading={savingStages} loadingText="Gemmer..." />
        </div>
      </Card>
    </>
  )
}
